import styles from "./index.module.css";
import React, { useCallback, useState, useMemo } from "react";
import classNames from "classnames";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import advancedFormat from "dayjs/plugin/advancedFormat";

import Button, { ButtonPurpose } from "shared/Button";
import Loader from "shared/Loader";
import TextArea from "shared/@forms/TextArea";
import Select from "shared/@forms/Select";
import { Auth } from "shared/@auth/Auth";
import createNotification from "shared/@utils/createNotification";

import TabActionBar from "shared/TabActionBar";

import { Notes } from "./useNotes";

// Get global formatting options from global config.
import { DATE_FORMAT, TIME_FORMAT } from "shared/globals";

const datetimeFormat = `${DATE_FORMAT} ${TIME_FORMAT} z`;

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

export default function ({ iboNumber }) {
  return (
    <Notes.Provider initialState={{ iboNumber }}>
      <NotesTab />
    </Notes.Provider>
  );
}

function NotesTab() {
  const { hasPermission } = Auth.useContainer();

  const {
    notes,
    isWaiting,
    isAdding,
    addFormIsOpen,
    setAddFormIsOpen,
  } = Notes.useContainer();

  const canUpdate = hasPermission("membership.ibo.update");

  return (
    <div className={styles.notes}>
      {canUpdate && (
        <TabActionBar>
          <Button
            disabled={isAdding || isWaiting}
            onClick={() => {
              setAddFormIsOpen(true);
            }}
          >
            Add Note
          </Button>
        </TabActionBar>
      )}

      {notes === null || isWaiting ? (
        <Loader />
      ) : (
        <>
          <div className={styles.notesHeader}>
            <div className={styles.dateHeader}>Date</div>
            <div className={styles.userHeader}>User</div>
            <div className={styles.noteHeader}>
              Note&nbsp;
              <span className={styles.fadedLabel}>
                &mdash; Click Note to Open
              </span>
            </div>
          </div>
          <div className={styles.col}>
            {addFormIsOpen && <AddNoteForm />}
            {notes.map((note, index) => (
              <Note key={index} note={note} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Note({ note }) {
  const [noteActive, setNoteActive] = useState(false);

  const { noteExceedsLength, notePreview, noteText } = useMemo(() => {
    return {
      noteExceedsLength: note.note.length > 160,
      notePreview: note.note.slice(0, 160),
      noteText: note.note.replace(/\n/g, "<br />"),
    };
  }, [note]);

  return (
    <div className={styles.note}>
      <div
        className={noteActive ? `${styles.expand}` : `${styles.compact}`}
        onClick={() => setNoteActive(!noteActive)}
      >
        <div className={styles.date}>
          {dayjs(note.createdOn).format(datetimeFormat)}
        </div>
        <div className={styles.user}> {note.wwgUser}</div>
        <div className={styles.noteContent}>
          <div className={styles.noteContainer} id="noteContainer">
            {noteActive ? (
              // Using dangerouslySetInnerHTML to render the `<br>`s we replaced above.
              <span dangerouslySetInnerHTML={{ __html: noteText }}></span>
            ) : (
              <>
                {notePreview}{" "}
                {noteExceedsLength && (
                  <span className={styles.fadedLabel}>...Read More</span>
                )}
              </>
            )}
          </div>

          <div className={noteActive ? `${styles.type}` : `${styles.hidden}`}>
            <div className={styles.typeHeader}>
              <span className={styles.bold}>Note Type: </span>
              {note.noteType}
            </div>
            <span className={classNames(styles.close, styles.fadedLabel)}>
              Click Note Again To Close &nbsp;
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function AddNoteForm() {
  const {
    noteTypes,
    addNote,
    isAdding,
    setAddFormIsOpen,
  } = Notes.useContainer();

  const [value, setValue] = useState("");
  const [noteType, setNoteType] = useState(noteTypes[0]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();

      // No double submissions. Buttons are disabled, but just in case.
      if (isAdding) {
        return;
      }

      if (!value) {
        createNotification({
          type: "info",
          title: "Empty Note",
          message: "Note Form Must Not Be Empty",
          autoDismiss: true,
        });
        return;
      }

      if (value.length > 2000) {
        createNotification({
          type: "info",
          title: "Note Too Long",
          message: "Note Length Is Restricted To 2000 Characters",
          autoDismiss: true,
        });
        return;
      }

      addNote({
        value: value,
        type: noteType.id,
      }).finally(() => {
        // Set value only after request is done to avoid note text appearing to disappear while saving.
        setValue("");
      });
    },
    [value, noteType]
  );

  const handleCancel = (e) => {
    e.preventDefault();
    setValue("");
    setAddFormIsOpen(false);
  };

  return (
    <div className={styles.addNoteForm}>
      <form onSubmit={handleSubmit} className={styles.addNoteFormInfo}>
        <div className={styles.addNoteDate}>
          {dayjs(Date.now()).format(datetimeFormat)}
        </div>
        <div className={styles.addNoteUser}></div>
        <div className={styles.form}>
          <TextArea
            placeholder="Enter new note..."
            autoFocus={true}
            disabled={isAdding}
            maxLength={2000}
            name="noteValue"
            className={styles.textArea}
            id="noteValue"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
          <div className={styles.selectRow}>
            <div className={styles.typeAddNote}>
              <Select
                name="newNoteType"
                id="newNoteType"
                value={noteType.id}
                disabled={isAdding}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  const type = noteTypes.find((t) => t.id === value);
                  setNoteType(type);
                }}
              >
                {noteTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </Select>
              <label htmlFor="newNoteType">Note Type</label>
            </div>
            <div className={styles.buttonRow}>
              <Button
                type="submit"
                purpose={ButtonPurpose.save}
                className={styles.formInput}
                disabled={isAdding || value.length === 0}
              >
                Add Note
              </Button>
              <Button
                className={styles.formInput}
                onClick={handleCancel}
                disabled={isAdding}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
